<template>
  <div>
    <div class="columns">
      <div class="column">
        <div class="group">
          <label>{{$t('item_snooze.product')}}</label>
          <AjaxSelect
              v-model="snooze.item"
              api="/api/entity/orderable/select"
              :query="{inventory: inventoryId}"
              :map-fn="(i) => i.name"
              :prop-class="{invalid: $validate.error('snooze.item.id')}"
          />
        </div>
        <div class="group">
          <label>{{$t('item_snooze.action.title')}}</label>
          <select v-model="action.selected">
            <option :value="a" v-for="a of action.all">{{$t(`item_snooze.action.${a}`)}}</option>
          </select>
        </div>
      </div>

      <div class="column" v-if="action.selected === 'outOfStock'">
        <div class="group">
          <label>{{$t('item_snooze.setOutOfStock')}}</label>
          <select v-model="setOutOfStock">
            <option :value="true">{{$t('yes')}}</option>
            <option :value="false">{{$t('no')}}</option>
          </select>
        </div>
      </div>

      <div class="column" v-if="action.selected === 'snooze'">
        <div class="group">
          <label>{{$t('item_snooze.timer')}}</label>
          <select v-model="timer">
            <option :value="24">24 {{$t('hours')}}</option>
            <option :value="48">48 {{$t('hours')}}</option>
            <option :value="72">72 {{$t('hours')}}</option>
            <option :value="null" disabled hidden>{{timer}} {{$t('hours')}}</option>
          </select>
        </div>
        <div class="group">
          <label>{{$t('start')}}</label>
          <datetime
            v-model="snoozeStartProxy"
            :use12-hour="hourFormat == '12'"
            value-zone="local"
            type="datetime"
            input-id="s_start"
            input-style="width: 100%"
            :minute-step="minuteStep"
            :min-datetime="now"
            :max-datetime="snoozeEndProxy"
          />
        </div>
        <div class="group">
          <label>{{$t('end')}}</label>
          <datetime
            v-model="snoozeEndProxy"
            :use12-hour="hourFormat == '12'"
            value-zone="local"
            type="datetime"
            input-id="s_end"
            input-style="width: 100%"
            :minute-step="minuteStep"
            :min-datetime="snoozeStartProxy"
            :max-datetime="maxRange"
          />
        </div>
      </div>
    </div>

    <div>
      <button v-if="action.selected === 'snooze'" class="btn btn-primary" @click="saveSnooze">{{$t('save')}}</button>
      <button v-if="action.selected === 'outOfStock'" class="btn btn-primary" @click="saveOutOfStock">{{$t('save')}}</button>
    </div>
  </div>
</template>

<script>
import {mapActions} from "vuex";
import {DateTime} from "luxon";
import {Core} from "@/core/Core";
import AjaxSelect from "@/components/tools/AjaxSelect.vue";

export default {
  name: "ItemSnoozeEdit",

  components: { AjaxSelect },

  data() {
    return {
      action: {
        all: ['snooze', 'outOfStock'],
        selected: 'snooze'
      },
      now: DateTime.local().toISO(),
      maxRange: DateTime.local().plus({days: 30}).toISO(),
      dateFormat: 'yyyy-MM-dd HH:mm:ss',
      minuteStep: 15,
      timer: 72,
      setOutOfStock: true,
      snooze: {
        id: null,
        item: {id: null},
        company: null,
        fromTime: null,
        toTime: null,
        snoozedOn: undefined,
        snoozedOff: undefined,
      }
    }
  },

  computed: {
    inventoryId() {
      return this.$store.state.contextInventory ? this.$store.state.contextInventory.id : null;
    },
    companyId() {
      return this.$store.state.contextCompany ? this.$store.state.contextCompany.id : null;
    },
    companyNameCanonical() {
      return this.$store.state.contextCompany ? this.$store.state.contextCompany.nameCanonical : null;
    },
    hourFormat() {
      if (this.$store.state.contextCompany) {
        return this.$store.state.contextCompany.hourFormat;
      }
      return "24";
    },
    snoozeStartProxy: {
      get() { return this.toIsoTime(this.snooze.fromTime) },
      set(value) { this.snooze.fromTime = this.fromIsoTime(value) }
    },
    snoozeEndProxy: {
      get() { return this.toIsoTime(this.snooze.toTime) },
      set(value) { this.snooze.toTime = this.fromIsoTime(value) }
    }
  },

  watch: {
    timer(value) {
      if (value && this.snooze.fromTime) {
        this.snooze.toTime = this.addTime(this.snooze.fromTime, value)
      }
    }
  },

  validate: {
    "snooze.item.id": {
      required: true
    }
  },

  mounted() {
    this.reset()
  },

  methods: {
    ...mapActions(["getItems"]),

    fromIsoTime(isoTime) {
      return isoTime ? DateTime.fromISO(isoTime).toFormat(this.dateFormat) : null
    },
    toIsoTime(crudeTime) {
      return crudeTime ? DateTime.fromFormat(crudeTime, this.dateFormat).toISO() : null
    },
    addTime(date, nbHours) {
      if (!date) {
        return
      }
      return DateTime.fromFormat(date, this.dateFormat)
          .plus({ hours: nbHours })
          .toFormat(this.dateFormat)
    },
    reset() {
      this.snooze.id = null;
      this.snooze.item = {id:null};
      this.snooze.company = null;
      this.snooze.snoozedOn = undefined;
      this.snooze.snoozedOff = undefined;
      if (!this.snooze.fromTime) {
        this.snooze.fromTime = DateTime.local().toFormat(this.dateFormat);
      }
      if (!this.snooze.toTime) {
        this.snooze.toTime = DateTime.local().plus({hours: this.timer}).toFormat(this.dateFormat);
      }
    },
    async saveOutOfStock() {
      if (!this.$validate.valid()) {
        this.$validate.validateAllOnce();
        return;
      }
      let data = {
        id: this.snooze.item.id,
        outOfStock: this.setOutOfStock
      };
      this.$store.commit('showSpinner', true);
      await Core.request('PUT', `/api/inventory/${this.companyNameCanonical}/orderables/${data.id}`, data);
      this.$store.commit('showSpinner', false);

      this.$emit('save');
      toast(this.$t("item_snooze.out_of_stock_saved"), "success");
    },
    async saveSnooze() {
      if (!this.$validate.valid()) {
        this.$validate.validateAllOnce();
        return;
      }
      if (!this.snooze.id) {
        this.snooze.company = this.companyId
      }
      try {
        let id = this.snooze.id;
        let clone = JSON.parse(JSON.stringify(this.snooze));
        clone.item = clone.item.id;

        this.$store.commit('showSpinner', true);
        await Core.request(
            (id ? 'PUT' : 'POST'),
            (id ? `/api/item_snoozes/${id}` : '/api/item_snoozes'),
            clone
        );
        this.reset();
        this.$emit('save');
        toast(this.$t("item_snooze.save_success"), "success");
      } catch (e) {
        console.log(e);
        toast(this.$t("error.no_server"), "error");
      }
      this.$store.commit('showSpinner', false);
    }
  }
}
</script>

<style lang="scss" scoped>

</style>
